import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import FileUploader from '@/components/Admin/UI/FileUploader';
import {
   SettingsField,
   SettingsLbl,
   SettingsInput,
   SettingsHintSm,
} from './ispSettingsUI';
import {
   PAYMENT_KINDS,
   PAYMENT_KIND_LABEL,
   type IspPaymentMethod,
   type IspPaymentKind,
} from './ispMockData';

/**
 * One way a customer can pay.
 *
 * Several are allowed (Ray, 2026-08-28) because operators really do take GCash,
 * Maya, a bank transfer and cash at the office all at once, and a suspended
 * customer should be shown every option rather than the one that happened to be
 * configured first.
 *
 * A method can be switched off rather than deleted: an operator who stops using
 * a wallet still has old payments recorded against it, and deleting the method
 * would orphan them.
 */
export type PaymentMethodDraft = Omit<IspPaymentMethod, 'id'>;

interface Props {
   existing?: IspPaymentMethod | null;
   onCancel: () => void;
   onSave: (draft: PaymentMethodDraft) => void;
}

const IspPaymentMethodForm: React.FC<Props> = ({
   existing,
   onCancel,
   onSave,
}) => {
   const [kind, setKind] = useState<IspPaymentKind>(existing?.kind ?? 'gcash');
   const [f, setF] = useState({
      accountName: existing?.accountName ?? '',
      accountNumber: existing?.accountNumber ?? '',
      instructions: existing?.instructions ?? '',
   });
   const [qrUrl, setQrUrl] = useState(existing?.qrUrl ?? '');
   const [active, setActive] = useState(existing?.active ?? true);
   const [uploading, setUploading] = useState(false);
   const [message, setMessage] = useState('');

   const set = (e: React.ChangeEvent<HTMLInputElement>) =>
      setF((old) => ({ ...old, [e.target.name]: e.target.value }));

   // Cash has no number and no QR — asking for them would be asking for
   // something that does not exist.
   const needsNumber = kind !== 'cash';
   const needsQr = kind === 'gcash' || kind === 'maya';

   const submit = () => {
      if (!f.accountName.trim()) {
         setMessage(
            kind === 'cash'
               ? 'Say where cash is accepted.'
               : 'Give the account name.'
         );
         return;
      }
      if (needsNumber && !f.accountNumber.trim()) {
         setMessage('Give the number customers should send to.');
         return;
      }
      setMessage('');
      onSave({
         kind,
         accountName: f.accountName.trim(),
         accountNumber: f.accountNumber.trim(),
         qrUrl,
         instructions: f.instructions.trim(),
         active,
      });
   };

   return (
      <Wrapper>
         <Grid>
            <SettingsField>
               <SettingsLbl>Method</SettingsLbl>
               <Sel
                  value={kind}
                  onChange={(e) => setKind(e.target.value as IspPaymentKind)}
               >
                  {PAYMENT_KINDS.map((k) => (
                     <option key={k} value={k}>
                        {PAYMENT_KIND_LABEL[k]}
                     </option>
                  ))}
               </Sel>
            </SettingsField>

            <SettingsField>
               <SettingsLbl>
                  {kind === 'cash' ? 'Where to pay' : 'Account name'}
               </SettingsLbl>
               <Field
                  name="accountName"
                  value={f.accountName}
                  placeholder={
                     kind === 'cash'
                        ? 'e.g. Office — Mabini St'
                        : 'The name on the account'
                  }
                  onChange={set}
               />
            </SettingsField>

            {needsNumber && (
               <SettingsField>
                  <SettingsLbl>
                     {kind === 'bank' ? 'Account number' : 'Number'}
                  </SettingsLbl>
                  <Field
                     name="accountNumber"
                     value={f.accountNumber}
                     placeholder={kind === 'bank' ? '0000 0000 0000' : '09xx xxx xxxx'}
                     onChange={set}
                  />
               </SettingsField>
            )}

            <SettingsField>
               <SettingsLbl>Shown to customers</SettingsLbl>
               <Toggle type="button" $on={active} onClick={() => setActive((v) => !v)}>
                  {active ? 'On' : 'Off'}
               </Toggle>
               <SettingsHintSm>
                  Switch off instead of deleting — old payments stay attached to
                  it.
               </SettingsHintSm>
            </SettingsField>

            <SettingsField $wide>
               <SettingsLbl>Instructions (optional)</SettingsLbl>
               <Field
                  name="instructions"
                  value={f.instructions}
                  placeholder="e.g. Send the screenshot after paying."
                  onChange={set}
               />
            </SettingsField>

            {needsQr && (
               <SettingsField $wide>
                  <SettingsLbl>QR code (optional)</SettingsLbl>
                  <FileUploader
                     value={qrUrl}
                     onChange={setQrUrl}
                     compact
                     onUploadingChange={setUploading}
                  />
               </SettingsField>
            )}
         </Grid>

         {message && <Err>{message}</Err>}

         <Actions>
            <Ghost type="button" onClick={onCancel}>
               Cancel
            </Ghost>
            <Primary type="button" disabled={uploading} onClick={submit}>
               {uploading ? 'Uploading…' : existing ? 'Save changes' : 'Add method'}
            </Primary>
         </Actions>
      </Wrapper>
   );
};

export default IspPaymentMethodForm;

const Wrapper = styled.div`
   width: 600px;
   max-width: 100%;
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 12px 16px;
   margin-top: 4px;

   @media (max-width: 720px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const Field = styled(SettingsInput)``;

const Sel = styled.select`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus {
      border-color: ${colors.PRIMARY};
   }
`;

const Toggle = styled.button<{ $on: boolean }>`
   align-self: flex-start;
   padding: 6px 14px;
   border-radius: 999px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   border: 1px solid ${({ $on }) => ($on ? colors.SUCCESS : colors.GRAY12)};
   background: ${({ $on }) => ($on ? colors.SUCCESS : 'transparent')};
   color: ${({ $on }) => ($on ? colors.WHITE : colors.GRAY3)};
`;

const Err = styled.p`
   margin: 10px 0 0;
   font-size: 12px;
   color: ${colors.DANGER};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 18px;
`;

const Primary = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;

   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;
